import React, { useState, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import FileUpload from '../../components/common/FileUpload';
import CountrySelector from '../../components/common/CountrySelector';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nContext';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import './CreateUserAccount.css';

function CreateUserAccount() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [userType, setUserType] = useState('expediteur');
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', telephone: '',
    enterpriseName: '', country: '', password: '', confirmPassword: ''
  });
  const [logo, setLogo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});

  const errors = useMemo(() => {
    const e = {};
    if (!form.firstName) e.firstName = t('register.errors.firstNameRequired');
    if (!form.lastName) e.lastName = t('register.errors.lastNameRequired');
    if (!form.email) e.email = t('register.errors.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = t('register.errors.emailInvalid');
    if (!form.telephone) e.telephone = t('register.errors.phoneRequired');
    if (!form.enterpriseName) e.enterpriseName = t('register.errors.enterpriseRequired');
    if (!form.password) e.password = t('register.errors.passwordRequired');
    else if (form.password.length < 6) e.password = t('register.errors.passwordMin');
    if (!form.confirmPassword) e.confirmPassword = t('register.errors.confirmPasswordRequired');
    else if (form.password !== form.confirmPassword) e.confirmPassword = t('register.errors.passwordsDoNotMatch');
    return e;
  }, [form, t]);

  const handleChange = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));
  const handleCountryChange = (val) => setForm(prev => ({ ...prev, country: val }));
  const handleBlur = (key) => () => setTouched(prev => ({ ...prev, [key]: true }));
  const hasError = (key) => touched[key] && errors[key];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ firstName: true, lastName: true, email: true, telephone: true, enterpriseName: true, password: true, confirmPassword: true });
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const endpoint = userType === 'expediteur'
        ? '/auth/register/expediteur'
        : userType === 'responsable_transporteur'
          ? '/auth/register/responsable-transporteur'
          : '/auth/register/transporteur';

      const formData = new FormData();
      formData.append('email', form.email);
      formData.append('password', form.password);
      formData.append('enterpriseName', form.enterpriseName);
      formData.append('firstname', form.firstName);
      formData.append('lastname', form.lastName);
      formData.append('telephone', form.telephone);
      if (form.country) formData.append('country', form.country);

      if (userType === 'responsable_transporteur') {
        formData.append('responsableTransporteur', 'true');
      }

      if (logo) {
        formData.append('logo', logo);
      }

      await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(t('common.success'));
      setTimeout(() => navigate('/admin/users'), 1000);
    } catch (err) {
      toast.error(err.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <ToastContainer />
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t('admin.createUserAccount')}</h1>
            <p className="text-muted small mb-0">{t('admin.userList.subtitle')}</p>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/admin/users')}>
            <i className="bi bi-arrow-left me-2"></i>{t('common.back')}
          </button>
        </div>

        <div className="card border-0 shadow-sm mx-auto" style={{ borderRadius: '0.9375rem', maxWidth: '56.25rem' }}>
          <div className="card-body p-4">
            <form onSubmit={handleSubmit}>
              <div className="row g-4">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('register.form.firstName')} *</label>
                  <input
                    type="text"
                    className={`form-control form-control-lg ${hasError('firstName') ? 'is-invalid' : ''}`}
                    style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                    placeholder="Prénom"
                    value={form.firstName}
                    onChange={handleChange('firstName')}
                    onBlur={handleBlur('firstName')}
                  />
                  {hasError('firstName') && <div className="invalid-feedback">{errors.firstName}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('register.form.lastName')} *</label>
                  <input
                    type="text"
                    className={`form-control form-control-lg ${hasError('lastName') ? 'is-invalid' : ''}`}
                    style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                    placeholder="Nom"
                    value={form.lastName}
                    onChange={handleChange('lastName')}
                    onBlur={handleBlur('lastName')}
                  />
                  {hasError('lastName') && <div className="invalid-feedback">{errors.lastName}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('register.form.email')} *</label>
                  <input
                    type="email"
                    className={`form-control form-control-lg ${hasError('email') ? 'is-invalid' : ''}`}
                    style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                    placeholder="Email"
                    value={form.email}
                    onChange={handleChange('email')}
                    onBlur={handleBlur('email')}
                  />
                  {hasError('email') && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('register.form.telephone')} *</label>
                  <input
                    type="tel"
                    className={`form-control form-control-lg ${hasError('telephone') ? 'is-invalid' : ''}`}
                    style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                    placeholder="Téléphone"
                    value={form.telephone}
                    onChange={handleChange('telephone')}
                    onBlur={handleBlur('telephone')}
                  />
                  {hasError('telephone') && <div className="invalid-feedback">{errors.telephone}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('register.form.enterpriseName')} *</label>
                  <input
                    type="text"
                    className={`form-control form-control-lg ${hasError('enterpriseName') ? 'is-invalid' : ''}`}
                    style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                    placeholder="Entreprise"
                    value={form.enterpriseName}
                    onChange={handleChange('enterpriseName')}
                    onBlur={handleBlur('enterpriseName')}
                  />
                  {hasError('enterpriseName') && <div className="invalid-feedback">{errors.enterpriseName}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('admin.userList.table.country')}</label>
                  <CountrySelector
                    value={form.country}
                    onChange={handleCountryChange}
                    className="form-select-lg"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('register.form.password')} *</label>
                  <input
                    type="password"
                    className={`form-control form-control-lg ${hasError('password') ? 'is-invalid' : ''}`}
                    style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                    placeholder="Mot de passe"
                    value={form.password}
                    onChange={handleChange('password')}
                    onBlur={handleBlur('password')}
                  />
                  {hasError('password') && <div className="invalid-feedback">{errors.password}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('register.form.confirmPassword')} *</label>
                  <input
                    type="password"
                    className={`form-control form-control-lg ${hasError('confirmPassword') ? 'is-invalid' : ''}`}
                    style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                    placeholder="Confirmer mot de passe"
                    value={form.confirmPassword}
                    onChange={handleChange('confirmPassword')}
                    onBlur={handleBlur('confirmPassword')}
                  />
                  {hasError('confirmPassword') && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">{t('register.roles.title')} *</label>
                  <select
                    className="form-select form-select-lg"
                    value={userType}
                    onChange={e => setUserType(e.target.value)}
                    disabled={submitting}
                    style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                  >
                    <option value="expediteur">{t('register.roles.expediteur')}</option>
                    <option value="transporteur">{t('register.roles.transporteur')}</option>
                    <option value="responsable_transporteur">{t('register.roles.transporteur')} (Responsable)</option>
                  </select>
                </div>

                {(userType === 'transporteur' || userType === 'responsable_transporteur') && (
                  <div className="col-12">
                    <label className="form-label fw-bold mb-3">{t('common.profile')}</label>
                    <FileUpload
                      id="logo-upload"
                      label={t('register.form.enterpriseName') + " Logo"}
                      value={logo}
                      onChange={setLogo}
                      maxSizeMB={2}
                      accept="image/png, image/jpeg, image/jpg"
                    />
                  </div>
                )}

                <div className="col-12 pt-3 border-top">
                  <button type="submit" className="btn btn-primary btn-lg px-5 shadow-sm w-100" style={{ borderRadius: '0.75rem' }} disabled={submitting}>
                    {submitting ? <div className="spinner-border spinner-border-sm me-2" role="status"></div> : <i className="bi bi-person-plus me-2"></i>}
                    {t('admin.createUserAccount')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default CreateUserAccount;
